import React, { useState } from 'react'; 

import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import clientsImage from '../assets/Clients.webp';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { trackEvent } from '../utils/analytics';

// Import Hero Dashboard Images
import heroAr from '../assets/hero/داش بورد AR.webp';
import heroEn from '../assets/hero/داش بورد EN.webp';

import { ArrowRight, Play } from 'lucide-react';

const Hero = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isAr = language === 'ar';
  const observe = useScrollAnimation();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const heroImage = isAr ? heroAr : heroEn;

  const titleParts = language === 'ar'
    ? {
        black: 'أدر تجارتك في كل مكان من',
        blue: 'لوحة تحكم واحدة'
      }
    : {
        black: 'Manage Your Business Everywhere from',
        blue: 'One Dashboard'
      };

  const merchantsCount = '2000+';


  return (
    <section className="pt-24 pb-16 bg-gradient-to-b from-white to-surface-app overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto">

          <div ref={observe} className="animate-fade-up flex items-center justify-center gap-3 sm:gap-4 mb-6">
            <div className="flex -space-x-2">
              <img src={clientsImage} alt="التجار" width="294" height="117" loading="lazy" className="h-10 sm:h-12 w-auto" />
            </div>
            <div className="flex items-baseline gap-1 sm:gap-2">
              <span className="text-xl sm:text-2xl font-bold text-nav">{merchantsCount}</span>
              <span className="text-sm sm:text-base text-body-secondary">{t.merchants}</span>
            </div>
          </div>

          <h1 ref={observe} className="animate-fade-up font-bold font-ibm mb-6 leading-tight max-w-[900px]" style={{ fontSize: 'clamp(36px, 5vw, 60px)', transitionDelay: '0.15s' }}>
            <span className="text-heading">{`${titleParts.black}\u00A0`}</span>
            <span className="text-primary">{titleParts.blue}</span>
          </h1>

          <p ref={observe} className="animate-fade-up font-almarai text-body-soft mb-8 leading-relaxed max-w-xl" style={{ fontSize: 'clamp(16px, 3vw, 20px)', transitionDelay: '0.3s' }}>
            {t.heroSubtitle}
          </p>

          <div ref={observe} className="animate-fade-up flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 w-full px-4 sm:px-0" style={{ transitionDelay: '0.45s' }}>
            <a
              href="https://platform.connecto-me.com/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-bold font-almarai text-base bg-dark text-white border-2 border-dark hover:bg-white hover:text-dark transition-all shadow-md inline-flex items-center justify-center gap-2"
            >
              <span>{t.heroCta1}</span>
              <ArrowRight size={18} className={isAr ? 'rotate-180' : ''} />
            </a>

            <button
              onClick={() => { trackEvent('video_opened', { video_name: 'connecto_demo', page_name: 'home' }); setIsVideoOpen(true); }}
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-bold font-almarai text-base bg-white text-dark border-2 border-edge-medium hover:border-dark transition-all shadow-sm inline-flex items-center justify-center gap-2"
            >
              <Play size={18} className="fill-current" />
              <span>{t.heroCta2}</span>
            </button>
          </div>
        </div>

        <div 
          ref={observe} 
          className="animate-scale-in relative mx-auto mt-8 w-full max-w-[462px] sm:max-w-[770px] lg:max-w-[1080px]" 
          style={{ transitionDelay: '0.6s' }}
        >
          <div className="absolute -inset-4 bg-gradient-to-r from-glow-blue/10 to-glow-purple/10 rounded-3xl blur-2xl"></div>

          <div className="relative w-full rounded-2xl shadow-2xl border border-edge overflow-hidden bg-surface-app/50 backdrop-blur-sm">
            <img
              src={heroImage}
              alt={isAr ? 'لوحة تحكم كونكتو' : 'Connecto Dashboard'}
              width="1920"
              height="1080"
              loading="eager"
              fetchPriority="high"
              className="w-full h-auto object-contain block"
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/5 to-transparent rounded-b-2xl pointer-events-none z-30"></div>
        </div>
      </div>
      
      {isVideoOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/80 backdrop-blur-sm" onClick={() => setIsVideoOpen(false)}>
          <div 
            className="relative w-full max-w-5xl bg-black rounded-2xl overflow-hidden shadow-2xl"
            style={{ maxHeight: 'calc(100vh - 40px)', aspectRatio: '16/9' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black/40 hover:bg-black/70 text-white rounded-full transition-all duration-200 backdrop-blur-md"
              aria-label="Close video"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <iframe 
              className="w-full h-full"
              src="https://www.youtube.com/embed/6yquVA_3sYg?autoplay=1"
              title="Connecto Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;